import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { Value } from 'slate'

import BlockTypes from '@slite/editor/lib/types/BlockTypes'

import ShareToModalProxy from './ShareToModalProxy'

import { ToolbarMoreIcon } from 'components/Utils/Icons'
import { getGreyIconByName } from '@slite/editor/lib/components/ApplicationIcons'

import ToolbarAvailableIntegrations from './ToolbarAvailableIntegrations'
import InstallableApplicationsTooltip from './InstallableApplicationsTooltip'
import BlockActionTypes from '@slite/editor/lib/types/BlockActionTypes'
import { Consumer as SharedEventBusContextConsumer } from 'contexts/sharedEventBus'
import ToolbarBottomPopover from './ToolbarBottomPopover'

const SHAREABLE_BLOCKS = [
  BlockTypes.H1,
  BlockTypes.H2,
  BlockTypes.UNSTYLED,
  BlockTypes.QUOTE,
  BlockTypes.OL_LI,
  BlockTypes.UL_LI,
  BlockTypes.TODO,
]

function renderEditor(props, editor, next) {
  const children = next()

  return (
    <Fragment>
      {children}
      <SharedEventBusContextConsumer>
        {eventBusStore => (
          <ShareToModalProxy editor={editor} eventBusStore={eventBusStore} />
        )}
      </SharedEventBusContextConsumer>
    </Fragment>
  )
}

export default ({
  ClipboardPlugin,
  utils: {
    makeInlineToolbarButton,
    makeBlockActionButton,
    TOOLBAR_BUTTON_VISIBILITY,
    isActiveBlock,
  },
}) => ({ getApplications }) => {
  renderEditor.propTypes = {
    value: PropTypes.instanceOf(Value).isRequired,
    readOnly: PropTypes.bool.isRequired,
    children: PropTypes.node.isRequired,
  }

  return [
    { renderEditor },

    makeInlineToolbarButton(BlockTypes.INTEGRATION_TILE, function() {
      const baseOptions = {
        index: 100,
        // This plugin groups its buttons together in the toolbar
        buttonGroup: 'integrations',
      }

      const { installed, notInstalled } = getApplications().reduce(
        (dict, application) => {
          if (application.isInstalled) {
            return {
              ...dict,
              installed: [...dict.installed, application],
            }
          }

          return {
            ...dict,
            notInstalled: [...dict.notInstalled, application],
          }
        },
        { installed: [], notInstalled: [] }
      )

      let entries = installed
        // Filter out applications without available Icons
        .reduce((applicationList, application) => {
          const Icon = getGreyIconByName(application.name)

          if (Icon) {
            return [...applicationList, { ...application, Icon }]
          }

          return applicationList
        }, [])
        .map(application => {
          const { id, name, integration, Icon } = application

          let onApply
          return {
            ...baseOptions,
            id,
            title: name,
            tooltipTitle: name,
            visibility: TOOLBAR_BUTTON_VISIBILITY
              ? TOOLBAR_BUTTON_VISIBILITY.EXPANDED_SELECTION
              : 0,
            isActive: editor =>
              editor.isActiveBlock
                ? editor.isActiveBlock(BlockTypes.INTEGRATION_TILE)
                : isActiveBlock(editor.value, BlockTypes.INTEGRATION_TILE),
            canApply: editor => editor.query('isMutableBlock'),
            onApply: () => onApply && onApply(),
            icon: ({ size }) => {
              return (
                <SharedEventBusContextConsumer>
                  {eventBusStore => {
                    onApply = () => {
                      const editor = eventBusStore.getItem('editor')
                      const text = ClipboardPlugin.serializers.textSerializer(
                        editor
                      )(editor.value.fragment)

                      eventBusStore.updateItem('event', {
                        type: 'showShareToModal',
                        metadata: {
                          selectedText: text,
                          integration: { ...integration, application },
                        },
                      })
                    }

                    return (
                      <section
                        key={id}
                        className="primary-on-hover"
                        style={{ position: 'relative' }}
                        onClick={onApply}
                      >
                        <Icon size={size} className="block" />
                      </section>
                    )
                  }}
                </SharedEventBusContextConsumer>
              )
            },
          }
        })

      if (notInstalled.length > 0) {
        entries = entries.concat({
          ...baseOptions,
          index: 200,
          title: `Install more integrations`,
          tooltipTitle: `Install more integrations`,
          icon: ({ size }) => {
            return (
              <ToolbarBottomPopover
                key={'installable'}
                content={() => {
                  return (
                    <div
                      className="radius-normal background-shade-100 shadow-hovering scrollable"
                      style={{ right: 0 }}
                    >
                      <SharedEventBusContextConsumer>
                        {eventBusStore => (
                          <InstallableApplicationsTooltip
                            eventBusStore={eventBusStore}
                          />
                        )}
                      </SharedEventBusContextConsumer>
                    </div>
                  )
                }}
              >
                <ToolbarMoreIcon size={size} />
              </ToolbarBottomPopover>
            )
          },
        })
      }

      return entries
    }),

    makeBlockActionButton(BlockActionTypes.SHARE, {
      type: BlockActionTypes.SHARE,
      component: ({ id }) => {
        return (
          <SharedEventBusContextConsumer key={id}>
            {eventBusStore => (
              <ToolbarAvailableIntegrations
                id={id}
                key={id}
                eventBusStore={eventBusStore}
                serializer={ClipboardPlugin.serializers.textSerializer}
              />
            )}
          </SharedEventBusContextConsumer>
        )
      },

      canApply: editor =>
        !editor.query('isEmptyBlock') &&
        (editor.isActiveBlock
          ? editor.isActiveBlock(SHAREABLE_BLOCKS)
          : isActiveBlock(editor.value, SHAREABLE_BLOCKS)),
      onClick: () => {}, //editor => editor.command('selectShareToTarget'),
    }),
  ]
}
